import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Eye, EyeOff, CheckCircle, Lock, XCircle } from 'lucide-react';
import PasswordStrengthIndicator from './registration/PasswordStrengthIndicator';
import { RegistrationLoading, RegistrationError } from './registration/RegistrationStates';
import IntroduceeProfileSection, { IntroduceeCountryCard } from './registration/IntroduceeProfileSection';
import { useRegistration } from './registration/useRegistration';

const RegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const {
    tokenData, loading, error, password, setPassword, confirmPassword, setConfirmPassword,
    username, usernameAvailable, checkingUsername, showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword, agreedToTerms, setAgreedToTerms,
    isSubmitting, passwordStrength, handlePasswordRegistration, handleUsernameChange, navigate,
    profileData, updateProfile, toggleProfileType,
  } = useRegistration(token);

  if (loading) return <RegistrationLoading />;
  if (error || !tokenData) return <RegistrationError error={error} onNavigate={() => navigate('/')} />;

  const isIntroduction = tokenData.source === 'introduction';
  const heading = isIntroduction ? "You've Been Introduced to MyInvestFolio" : 'Complete Your Registration';
  const subheading = isIntroduction
    ? 'Your access has been pre-approved. Set up your account below.'
    : 'Your access has been approved. Create your account below.';

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black py-8 sm:py-12 px-4">
      <div className="max-w-lg sm:max-w-2xl mx-auto space-y-6 sm:space-y-8">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-10 h-10 text-emerald-600" /></div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white" data-testid="reg-heading">{heading}</h1>
          <p className="text-gray-400" data-testid="reg-subheading">{subheading}</p>
        </div>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center"><span className="text-emerald-600 font-bold text-lg">{tokenData.first_name?.[0]}{tokenData.last_name?.[0]}</span></div>
              <div><p className="text-white font-semibold">{tokenData.first_name} {tokenData.last_name}</p><p className="text-gray-400 text-sm">{tokenData.email}</p></div>
            </div>
          </CardContent>
        </Card>
        {isIntroduction && (
          <>
            <IntroduceeCountryCard profileData={profileData} onChange={updateProfile} />
            <IntroduceeProfileSection
              profileData={profileData}
              onChange={updateProfile}
              onToggleType={toggleProfileType}
            />
          </>
        )}
        <div className="space-y-6">
          <Card className="bg-gray-800 border-emerald-500 border-2">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><Lock className="w-5 h-5 text-emerald-600" /></div>
                <div><CardTitle className="text-white">Create Your Account</CardTitle><CardDescription className="text-gray-400">Full platform access with secure password</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordRegistration} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-gray-300">Username <span className="text-gray-500 text-sm">(Optional)</span></Label>
                  <div className="relative">
                    <Input id="username" type="text" value={username} onChange={handleUsernameChange} placeholder="Choose a unique username" className="bg-gray-700 border-gray-600 text-white pr-10" />
                    {checkingUsername && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-gray-400" />}
                    {!checkingUsername && usernameAvailable === true && username && <CheckCircle className="absolute right-3 top-3 w-4 h-4 text-green-500" />}
                    {!checkingUsername && usernameAvailable === false && username && <XCircle className="absolute right-3 top-3 w-4 h-4 text-red-500" />}
                  </div>
                  <p className="text-xs text-gray-400">You can login with either your email or username. Must be 3-20 characters.</p>
                  {usernameAvailable === false && username && <p className="text-xs text-red-400">Username already taken</p>}
                  {usernameAvailable === true && username && <p className="text-xs text-green-400">Username available!</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-gray-700 border-gray-600 text-white pr-10" placeholder="Enter password" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                {password && <PasswordStrengthIndicator passwordStrength={passwordStrength} />}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                  <div className="relative">
                    <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-gray-700 border-gray-600 text-white pr-10" placeholder="Confirm password" required />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">{showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                  {confirmPassword && password !== confirmPassword && <p className="text-red-500 text-xs">Passwords do not match</p>}
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox id="terms" checked={agreedToTerms} onCheckedChange={setAgreedToTerms} className="mt-1" />
                  <label htmlFor="terms" className="text-sm text-gray-400 cursor-pointer">I agree to the Terms and Conditions and Privacy Policy</label>
                </div>
                <Button type="submit" disabled={isSubmitting || !agreedToTerms || password !== confirmPassword || passwordStrength.score < 4} className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg font-semibold disabled:opacity-50">
                  {isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Creating Account...</>) : 'Create Account'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
